import React, { useState, useEffect } from "react";
import { CModal } from "@coreui/react";

interface BlockUserRequest {
  blockedAccess: string;
  title: {
    fr: string;
    en: string;
    ar: string;
  };
  description: {
    fr: string;
    en: string;
    ar: string;
  };
  until: string;
}

interface Props {
  onClose: () => void;
  onSubmit: (data?: BlockUserRequest) => void;
  open: boolean;
  loading?: boolean;
  initialData?: BlockUserRequest;
  action?: "block";
}

const blockedAccessOptions = [
  { value: "venez", label: "Venez" },
  { value: "venez-taxi", label: "Venez Taxi" },
];

export default function BlockUserModal({
  onClose,
  onSubmit,
  open,
  loading,
  initialData,
  action = "block",
}: Props) {
  const [formData, setFormData] = useState<BlockUserRequest>({
    blockedAccess: "",
    title: {
      fr: "",
      en: "",
      ar: "",
    },
    description: {
      fr: "",
      en: "",
      ar: "",
    },
    until: "",
  });

  // Reset form when modal opens or initialData changes
  useEffect(() => {
    if (open) {
      // Reset to empty form for new block
      setFormData({
        blockedAccess: "",
        title: { fr: "", en: "", ar: "" },
        description: { fr: "", en: "", ar: "" },
        until: "",
      });
    }
  }, [open, initialData, action]);

  const handleInputChange = (field: string, value: string, lang?: string) => {
    if (lang) {
      setFormData((prev) => ({
        ...prev,
        [field]: {
          ...prev[
            field as keyof Pick<BlockUserRequest, "title" | "description">
          ],
          [lang]: value,
        },
      }));
    } else {
      setFormData((prev) => ({
        ...prev,
        [field]: value,
      }));
    }
  };

  const handleSubmit = () => {
    // Basic validation for block action
    if (
      !formData.blockedAccess ||
      !formData.until ||
      !formData.title.fr ||
      !formData.description.fr
    ) {
      alert("Please fill in all required fields");
      return;
    }
    // console.log(formData);
    onSubmit(formData);
  };

  const formatDateTimeLocal = (isoString: string) => {
    if (!isoString) return "";
    const date = new Date(isoString);
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, "0");
    const day = String(date.getDate()).padStart(2, "0");
    const hours = String(date.getHours()).padStart(2, "0");
    const minutes = String(date.getMinutes()).padStart(2, "0");
    return `${year}-${month}-${day}T${hours}:${minutes}`;
  };

  const handleDateTimeChange = (value: string) => {
    if (value) {
      const isoString = new Date(value).toISOString();
      handleInputChange("until", isoString);
    } else {
      handleInputChange("until", "");
    }
  };

  const modalTitle = "Bloquer un utilisateur";
  const submitButtonText = "Bloquer";
  const submitButtonClass = "btn btn-danger";

  return (
    <CModal alignment="center" visible={open} size="lg">
      <div className="d-flex flex-column p-4">
        <h5 className="mb-4 fw-bold text-center">{modalTitle}</h5>

        {
          <>
            <div className="mb-3">
              <label className="form-label fw-semibold">
                Type de blocage *
              </label>
              <select
                className="form-select"
                value={formData.blockedAccess}
                onChange={(e) =>
                  handleInputChange("blockedAccess", e.target.value)
                }
                disabled={loading}
              >
                <option value="">Sélectionner le type de blocage...</option>
                {blockedAccessOptions.map((option) => (
                  <option key={option.value} value={option.value}>
                    {option.label}
                  </option>
                ))}
              </select>
            </div>

            <div className="mb-3">
              <label className="form-label fw-semibold">Titre *</label>
              <div className="row g-2">
                <div className="col-md-4">
                  <input
                    type="text"
                    className="form-control"
                    placeholder="Français"
                    value={formData.title.fr}
                    onChange={(e) =>
                      handleInputChange("title", e.target.value, "fr")
                    }
                    required={true}
                    disabled={loading}
                  />
                  <small className="text-muted">
                    FR (Ce champ est obligatoire)
                  </small>
                </div>
                <div className="col-md-4">
                  <input
                    type="text"
                    className="form-control"
                    placeholder="Anglais"
                    value={formData.title.en}
                    onChange={(e) =>
                      handleInputChange("title", e.target.value, "en")
                    }
                    disabled={loading}
                  />
                  <small className="text-muted">EN</small>
                </div>
                <div className="col-md-4">
                  <input
                    type="text"
                    className="form-control"
                    placeholder="Arabe"
                    value={formData.title.ar}
                    onChange={(e) =>
                      handleInputChange("title", e.target.value, "ar")
                    }
                    disabled={loading}
                  />
                  <small className="text-muted">AR</small>
                </div>
              </div>
            </div>

            <div className="mb-3">
              <label className="form-label fw-semibold">Description *</label>
              <div className="row g-2">
                <div className="col-md-4">
                  <textarea
                    className="form-control"
                    rows={3}
                    placeholder="Description en français"
                    value={formData.description.fr}
                    onChange={(e) =>
                      handleInputChange("description", e.target.value, "fr")
                    }
                    required={true}
                    disabled={loading}
                  />
                  <small className="text-muted">
                    FR (Ce champ est obligatoire)
                  </small>
                </div>
                <div className="col-md-4">
                  <textarea
                    className="form-control"
                    rows={3}
                    placeholder="Description en anglais"
                    value={formData.description.en}
                    onChange={(e) =>
                      handleInputChange("description", e.target.value, "en")
                    }
                    disabled={loading}
                  />
                  <small className="text-muted">EN</small>
                </div>
                <div className="col-md-4">
                  <textarea
                    className="form-control"
                    rows={3}
                    placeholder="Description en arabe"
                    value={formData.description.ar}
                    onChange={(e) =>
                      handleInputChange("description", e.target.value, "ar")
                    }
                    disabled={loading}
                  />
                  <small className="text-muted">AR</small>
                </div>
              </div>
            </div>

            <div className="mb-4">
              <label className="form-label fw-semibold">
                Jusqu'à la date et l'heure *
              </label>
              <input
                type="datetime-local"
                className="form-control"
                value={formatDateTimeLocal(formData.until)}
                onChange={(e) => handleDateTimeChange(e.target.value)}
                disabled={loading}
              />
            </div>
          </>
        }

        <div className="d-flex align-items-center justify-content-center gap-4">
          <button
            onClick={handleSubmit}
            className={`${submitButtonClass} text-white text-uppercase shadow-primary`}
            style={{ width: "140px" }}
            disabled={loading}
          >
            {loading ? "..." : submitButtonText}
          </button>
          <button
            onClick={onClose}
            className="btn btn-secondary text-white text-uppercase shadow-secondary"
            style={{ width: "140px" }}
            disabled={loading}
          >
            Annuler
          </button>
        </div>
      </div>
    </CModal>
  );
}
